import React from 'react';
import { t } from 'i18next';

interface TimeSlotOption {
  value: string;
  label: string;
}

interface TimeSlotCardsProps {
  options: TimeSlotOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  isLoading: boolean;
  forceLight?: boolean; // Nouveau prop pour forcer le mode clair
}

const TimeSlotCards: React.FC<TimeSlotCardsProps> = ({
  options,
  selectedValue,
  onSelect,
  isLoading,
  forceLight = false // Par défaut, suit le système
}) => {
  // Fonction pour obtenir les classes avec ou sans mode sombre
  const getClasses = (lightClass: string, darkClass: string) => {
    return forceLight ? lightClass : `${lightClass} ${darkClass}`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        <span className={`ml-3 text-sm ${getClasses('text-gray-600', 'dark:text-gray-400')}`}>
          {t('dashboard.loading1', 'Chargement des horaires...')}
        </span>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className={`text-sm text-center mt-1 p-4 ${getClasses('text-amber-600', 'dark:text-amber-500')}`}>
        {t('dashboard.noSlots', 'Aucun créneau disponible pour cette date. Veuillez choisir une autre date ou un autre service.')}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <h3 className={`text-sm font-medium mb-3 ${getClasses('text-gray-700', 'dark:text-gray-300')}`}>
        {t('dashboard.availableSlots', 'Horaires disponibles')}
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {options.map((option) => {
          const isSelected = selectedValue === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              className={`
                relative group flex items-center justify-center rounded-full px-4 py-3 sm:py-2 transition-all duration-200
                ${isSelected
                  ? 'bg-primary text-white shadow-md'
                  : `${getClasses('bg-white hover:bg-gray-50 text-gray-700 border-gray-200', 'dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 dark:border-gray-700')} border`}
              `}
            >
              <div className={`
                absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-full transition-all duration-200
                ${isSelected ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}
              `}></div>
              
              <span className="text-sm font-medium whitespace-nowrap">
                {option.label}
              </span>
              
              {isSelected && (
                <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-primary ${getClasses('bg-white', 'dark:bg-primary')}`}></div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TimeSlotCards;